import React, { useEffect, useState } from "react";
import { StatsApiResponse, QuestionnaireReportApiResponse } from "../../types";
import { getApiUrl } from "../../config/api";
import { authFetch } from "../../config/authFetch";
import { useApi } from "../../context/ApiContext";
import { useProduct } from "../../context/ProductContext";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [totalCategoryCount, setTotalCategoryCount] = useState(0);
  const [totalSubcategoryCount, setTotalSubcategoryCount] = useState(0);
  const [totalTemplateCount, setTotalTemplateCount] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeUsers, setActiveUsers] = useState(0);
  const [inactiveUsers, setInactiveUsers] = useState(0);
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireReportApiResponse["data"] | null>(null);
  const { refreshTrigger, isRefreshing } = useApi();
  const { product } = useProduct();

  useEffect(() => {
    // Ignore responses that arrive after the product or environment changed.
    let cancelled = false;

    // Each source fetches independently so one failing request never blocks the
    // others (previously a single failed fetch aborted the whole dashboard).
    const fetchTemplateStats = async () => {
      try {
        const res = await fetch(getApiUrl("/api/templates/v1.0.0/stats"));
        if (!res.ok) return;
        const result: StatsApiResponse = await res.json();
        if (!cancelled && result.status === "success" && Array.isArray(result.data)) {
          setTotalCategoryCount(result.data.length);
          setTotalSubcategoryCount(
            result.data.reduce((sum, c) => sum + c.totalSubcategories, 0)
          );
          setTotalTemplateCount(
            result.data.reduce((sum, c) => sum + c.totalTemplates, 0)
          );
        }
      } catch (error) {
        console.error("Error fetching template stats:", error);
      }
    };

    const fetchUserStats = async () => {
      try {
        const res = await authFetch(
          getApiUrl(`/api/auth/v1.0.0/users?page=1&pageSize=1&product=${product}`),
          { headers: { accept: "application/json" } }
        );
        if (!res.ok) return;
        const result = await res.json();
        if (!cancelled && result.status === "success" && result.data?.counts) {
          setTotalUsers(result.data.counts.total);
          setActiveUsers(result.data.counts.active);
          setInactiveUsers(result.data.counts.inactive);
        }
      } catch (error) {
        console.error("Error fetching user stats:", error);
      }
    };

    const fetchQuestionnaire = async () => {
      try {
        const res = await fetch(
          getApiUrl("/api/questionnaire/v1.0.0/report?context=onboarding")
        );
        if (!res.ok) return;
        const result: QuestionnaireReportApiResponse = await res.json();
        if (!cancelled && result.status === "success" && result.data) {
          setQuestionnaireData(result.data);
        }
      } catch (error) {
        console.error("Error fetching questionnaire report:", error);
      }
    };

    const fetchDashboardData = async () => {
      setLoading(true);
      await Promise.allSettled([
        fetchTemplateStats(),
        fetchUserStats(),
        fetchQuestionnaire(),
      ]);
      if (!cancelled) setLoading(false);
    };

    fetchDashboardData();
    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, product]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {loading || isRefreshing ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold opacity-90">
                    Total Categories
                  </h2>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                  </div>
                </div>
                <p className="text-5xl font-extrabold mb-2">
                  {totalCategoryCount}
                </p>
                <p className="text-sm opacity-80">Active categories</p>
              </div>
            </div>

            <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold opacity-90">
                    Total Subcategories
                  </h2>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-5xl font-extrabold mb-2">
                  {totalSubcategoryCount}
                </p>
                <p className="text-sm opacity-80">Across all categories</p>
              </div>
            </div>

            <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold opacity-90">
                    Total Templates
                  </h2>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-5xl font-extrabold mb-2">
                  {totalTemplateCount}
                </p>
                <p className="text-sm opacity-80">Ready to use</p>
              </div>
            </div>

            <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold opacity-90">
                    Total Users
                  </h2>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                  </div>
                </div>
                <p className="text-5xl font-extrabold mb-2">
                  {totalUsers}
                </p>
                <p className="text-sm opacity-80">{activeUsers} active, {inactiveUsers} inactive</p>
              </div>
            </div>
          </div>

          {/* Questionnaire Reports Section */}
          {questionnaireData ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-bold text-gray-900">Questionnaire Reports</h2>
                <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                  {questionnaireData.summary.context}
                </span>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold opacity-90">
                        Total Questions
                      </h2>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5zM15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-5xl font-extrabold mb-2">
                      {questionnaireData.summary.totalQuestions}
                    </p>
                    <p className="text-sm opacity-80">Questions in questionnaire</p>
                  </div>
                </div>

                <div className="group relative p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-rose-500 via-pink-500 to-red-500 text-white overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold opacity-90">
                        Total Answers
                      </h2>
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                        <svg
                          className="w-6 h-6"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-5xl font-extrabold mb-2">
                      {questionnaireData.summary.totalAnswers.toLocaleString()}
                    </p>
                    <p className="text-sm opacity-80">Total responses collected</p>
                  </div>
                </div>
              </div>

              {/* Questions List */}
              <div className="space-y-6">
                {questionnaireData.questions.map((question) => {
                  const maxCount = Math.max(...question.answerDistribution.map(a => a.userCount));
                  return (
                    <div
                      key={question.questionId}
                      className="p-6 rounded-2xl shadow-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 hover:shadow-2xl transition-all duration-300"
                    >
                      <div className="mb-6">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-xl font-bold text-gray-900 flex-1">
                            {question.question}
                          </h3>
                          <div className="flex gap-2 ml-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {question.totalResponses.toLocaleString()} Responses
                            </span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              {question.uniqueUserCount.toLocaleString()} Users
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {question.type}
                          </span>
                          {question.isRequired && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                              Required
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Answer Distribution */}
                      <div className="space-y-4">
                        {question.answerDistribution.map((answer, index) => {
                          const percentage = maxCount > 0 ? (answer.userCount / maxCount) * 100 : 0;
                          const answerPercentage = question.totalResponses > 0 
                            ? (answer.userCount / question.totalResponses) * 100 
                            : 0;
                          const colors = [
                            "from-blue-500 to-cyan-500",
                            "from-purple-500 to-pink-500",
                            "from-green-500 to-emerald-500",
                            "from-orange-500 to-red-500",
                            "from-indigo-500 to-blue-500",
                            "from-pink-500 to-rose-500",
                          ];
                          const colorClass = colors[index % colors.length];

                          return (
                            <div key={answer.value} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${colorClass}`}></div>
                                  <span className="text-sm font-semibold text-gray-800">
                                    {answer.label}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-gray-700">
                                    {answer.userCount.toLocaleString()}
                                  </span>
                                  <span className="text-xs text-gray-500 min-w-[3rem] text-right">
                                    {answerPercentage.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                  className={`h-full bg-gradient-to-r ${colorClass} rounded-full transition-all duration-500 ease-out`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-lg text-gray-500">No questionnaire data available</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
